import { useDeleteVenueOverviewVersion } from '@api';
import { useNotification } from '@common';
import { modals } from '@mantine/modals';
import { useRouter } from '@tanstack/react-router';
import { AxiosError } from 'axios';
import { useTranslation } from 'react-i18next';

const TRANS_PATH = 'OVERVIEW_VERSION';
const TRANS_PATH_NOTIFICATION = 'NOTIFICATIONS.DELETE';

type UseDeleteOverviewVersionReturnType = {
  handleDelete: (version: string) => void;
};

type UseDeleteOverviewVersionArgsType = {
  venueId: string;
};

export const useDeleteOverviewVersion = ({
  venueId,
}: UseDeleteOverviewVersionArgsType): UseDeleteOverviewVersionReturnType => {
  const { t } = useTranslation('venueDetailsOverview');
  const { showSuccess, showError } = useNotification();
  const router = useRouter();
  const { mutate } = useDeleteVenueOverviewVersion();

  const onSuccess = async (): Promise<void> => {
    showSuccess({
      title: `${TRANS_PATH_NOTIFICATION}.SUCCESS.TITLE`,
      message: `${TRANS_PATH_NOTIFICATION}.SUCCESS.MESSAGE`,
    });
    router.navigate({
      to: '/venues/$id/overview',
      params: { id: venueId },
    });
  };

  const onError = (error: AxiosError): void => {
    showError({
      title: `${TRANS_PATH_NOTIFICATION}.ERROR.TITLE`,
      message: `${TRANS_PATH_NOTIFICATION}.ERROR.MESSAGE`,
      error,
    });
  };

  const onConfirm = (overviewVersion: string) => {
    mutate(
      {
        venueId,
        overviewVersion,
      },
      {
        onSuccess,
        onError,
      }
    );
  };

  const handleDelete = (version: string) => {
    modals.openConfirmModal({
      title: t(`${TRANS_PATH}.DELETE_CONFIRMATION_TITLE`),
      labels: {
        confirm: t(`${TRANS_PATH}.DELETE_CONFIRMATION_BUTTON`),
        cancel: t('CANCEL_BUTTON', { ns: 'common' }),
      },
      confirmProps: {
        color: 'red',
      },
      onConfirm: () => onConfirm(version),
    });
  };

  return { handleDelete };
};
